import moment from 'moment';
import 'moment-timezone';

/**
 * Check if a hall is available for a specific reservation time based on its opening hours
 * @param {Object} hall - The hall object
 * @param {Array} openHours - Array of opening hours records
 * @param {Date|string} reservationTime - The reservation time in UTC
 * @param {string} timezone - The restaurant's timezone (e.g., 'America/New_York')
 * @returns {boolean} - True if hall is available, false otherwise
 */
export const isHallAvailableForReservation = (hall, openHours, reservationTime, timezone) => {
  // If hall has no openHours records, it's always available
  const hallOpenHours = openHours.filter(oh => oh.hallId === hall.id);
  if (hallOpenHours.length === 0) {
    return true;
  }

  // Convert UTC reservation time to restaurant's local timezone
  const localReservationTime = moment.utc(reservationTime).tz(timezone);

  // Get the day of week for the reservation (1 = Monday, 7 = Sunday)
  const reservationDayOfWeek = localReservationTime.isoWeekday().toString();

  // Find the openHours record for this hall and day of week
  const dayOpenHours = hallOpenHours.find(oh => oh.dayOfWeek === reservationDayOfWeek);

  // If no openHours for this day or hall works 24 hours, hall is available
  if (!dayOpenHours || dayOpenHours.works24h) {
    return true;
  }

  // If hall is closed on this day
  if (dayOpenHours.closed) {
    return false;
  }

  // Get reservation time in minutes since midnight (in local timezone)
  const reservationHour = localReservationTime.hour();
  const reservationMinute = localReservationTime.minute();
  const reservationTimeInMinutes = reservationHour * 60 + reservationMinute;

  // Get opening and closing times in minutes since midnight
  const openTimeInMinutes = dayOpenHours.openH * 60 + dayOpenHours.openM;
  const closeTimeInMinutes = dayOpenHours.closedH * 60 + dayOpenHours.closedM;

  // Handle cases where closing time is after midnight (e.g., 02:00)
  if (closeTimeInMinutes < openTimeInMinutes) {
    // Closing time is after midnight
    // Reservation should be either after opening time OR before closing time
    return reservationTimeInMinutes >= openTimeInMinutes || reservationTimeInMinutes <= closeTimeInMinutes;
  } else {
    // Normal case: closing time is on the same day
    return reservationTimeInMinutes >= openTimeInMinutes && reservationTimeInMinutes <= closeTimeInMinutes;
  }
};

/**
 * Filter halls to get only those available for a specific reservation time
 * @param {Array} halls - Array of hall objects
 * @param {Array} openHours - Array of opening hours records
 * @param {Date|string} reservationTime - The reservation time in UTC
 * @param {string} timezone - The restaurant's timezone (e.g., 'America/New_York')
 * @returns {Array} - Array of available halls
 */
export const getAvailableHalls = (halls, openHours, reservationTime, timezone) => {
  if (!halls || !Array.isArray(halls)) {
    return [];
  }

  return halls.filter(hall => {
    if (!hall) return false;
    return isHallAvailableForReservation(hall, openHours, reservationTime, timezone);
  });
};

/**
 * Get opening hours for halls only (filter out non-hall opening hours)
 * @param {Array} openHours - Array of all opening hours
 * @returns {Array} - Array of hall opening hours only
 */
export const getHallOpenHours = (openHours) => {
  if (!openHours || !Array.isArray(openHours)) {
    return [];
  }

  return openHours.filter(o => o.hallId);
};
